import type { ReactNode } from "react";
import { cn } from "../lib/cn.ts";
import { useSlidingPill } from "../lib/use-sliding-pill.ts";

/**
 * Controle segmentado das Settings do Ofative (Start of week, tema, 12/24h).
 *
 * A pílula desliza entre as opções — a mesma transição dos tipos de agenda.
 */

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: Array<SegmentedOption<T>>;
  "aria-label": string;
  size?: "sm" | "md";
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  "aria-label": ariaLabel,
  size = "md",
}: SegmentedControlProps<T>) {
  const { listRef, pill } = useSlidingPill(value);

  return (
    <div
      ref={listRef}
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        "relative inline-flex items-stretch overflow-hidden rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-subtle)] p-0.5",
        size === "sm" ? "h-8" : "h-9",
      )}
    >
      <span
        aria-hidden="true"
        data-ready={pill.ready ? "true" : "false"}
        className="dream-pill"
        style={{ left: pill.left, width: pill.width, top: pill.top, height: pill.height }}
      />
      {options.map((option) => {
        const selected = option.value === value;
        return (
          // biome-ignore lint/a11y/useSemanticElements: controle segmentado do kit, não input radio nativo.
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            data-pill-active={selected ? "true" : undefined}
            onClick={() => onChange(option.value)}
            className={cn(
              "relative z-[1] inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 text-[12px] font-semibold tracking-[0]",
              "transition-[color] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
              selected
                ? "text-[#0074FF]"
                : "text-[var(--color-ink-secondary)] hover:text-[var(--color-ink)]",
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
